import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import PullToRefresh from "react-simple-pull-to-refresh";

import { CalendarDays } from "lucide-react";
import { VenueConfig } from "../../../../../types/venues";
import { filterBookings } from "../../../../../utils/booking";
import { DiaryBookingCard } from "./DiaryBookingCard/DiaryBookingCard";
import { DiaryBookingCardPlaceholder } from "./DiaryBookingCardPlaceholder/DiaryBookingCardPlaceholder";

import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { EDSSpinner } from "../../../../eds/EDSSpinner";

import { ViewBooking } from "collins-js-sdk";
import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";
import { VenueGroupConfig } from "../../../../../types/VenueGroup";
import { waitlistBookingsFilter } from "../../../../../utils/bookingFilters";
import { Divider } from "./Divider";
import { RunSheetCard } from "./RunSheetCard/RunSheetCard";

// Tailwind class constants for DiaryBookingCardList
const DIARY_BOOKING_CARD_LIST_CLASSES =
  "DiaryBookingCardList h-full w-full relative flex flex-col overflow-hidden min-h-[600px]";
const SKELETON_OVERLAY_CLASSES =
  "DiaryBookingCardList__skeleton-overlay absolute inset-0 w-full h-full overflow-auto z-10 bg-background border-none outline-none shadow-none";
const CONTENT_CLASSES =
  "DiaryBookingCardList__content absolute inset-0 w-full h-full overflow-hidden flex flex-col";

type DiaryBookingCardListProps = {
  bookings: any[] | undefined;
  venueConfig: VenueConfig | null;
  venueGroupConfig: VenueGroupConfig | null;
  initialFilterQuery: CallableFunction;
  showFilterRow?: boolean;
  isInitialising?: boolean;
  isViewTransitioning?: boolean;
  showTimeDiviers?: boolean;
  isOnRunSheet?: boolean;
};

// Constants for stable heights
const BOOKING_CARD_HEIGHT = 100;
const WAITLISTED_CARD_HEIGHT = 130;
const DIVIDER_HEIGHT = 40;
const BUFFER_SIZE = 5; // Number of items to render above/below viewport

// Simple virtual scrolling implementation
const VirtualList = React.memo(
  ({
    items,
    itemHeight,
    height,
    renderItem
  }: {
    items: any[];
    itemHeight: (index: number) => number;
    height: number;
    renderItem: (
      item: any,
      index: number,
      style: React.CSSProperties
    ) => React.ReactElement;
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Calculate visible range
    const totalHeight = items.reduce(
      (acc, _, index) => acc + itemHeight(index),
      0
    );
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / BOOKING_CARD_HEIGHT) - BUFFER_SIZE
    );
    const endIndex = Math.min(
      items.length,
      Math.ceil((scrollTop + height) / BOOKING_CARD_HEIGHT) + BUFFER_SIZE
    );

    // Calculate offset for visible items
    const offsetY = items
      .slice(0, startIndex)
      .reduce((acc, _, index) => acc + itemHeight(index), 0);

    const visibleItems = items.slice(startIndex, endIndex);

    return (
      <div
        ref={containerRef}
        style={{
          height,
          overflow: "auto",
          position: "relative",
          paddingBottom: "20px" // Add bottom padding to ensure last item is fully visible when scrolled
        }}
        onScroll={handleScroll}
      >
        <div style={{ height: totalHeight, position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: offsetY,
              left: 0,
              right: 0,
              paddingBottom: "20px" // Add bottom padding to ensure last item is fully visible
            }}
          >
            {visibleItems.map((item, index) => {
              const actualIndex = startIndex + index;
              const itemTop = items
                .slice(0, actualIndex)
                .reduce((acc, _, idx) => acc + itemHeight(idx), 0);

              return (
                <div
                  key={actualIndex}
                  style={{
                    position: "absolute",
                    top: itemTop - offsetY,
                    left: 0,
                    right: 0,
                    height: itemHeight(actualIndex)
                  }}
                >
                  {renderItem(item, actualIndex, {
                    height: itemHeight(actualIndex)
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

VirtualList.displayName = "VirtualList";

export const DiaryBookingCardList = ({
  bookings,
  venueConfig,
  venueGroupConfig,
  initialFilterQuery,
  isInitialising,
  isViewTransitioning,
  showTimeDiviers,
  isOnRunSheet
}: DiaryBookingCardListProps) => {
  const diaryContext = useDiaryContext();
  const [unfilteredBookings, setUnfilteredBookings] = useState<
    any[] | undefined
  >();
  const [filteredBookings, setFilteredBookings] = useState<any[] | undefined>();

  // Dynamic viewport height calculation
  const [viewportHeight, setViewportHeight] = useState(
    () => window.innerHeight - 220
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight - 220);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [cardItemIsDragging, setCardItemIsDragging] = useState<boolean>(false);
  const [activePopoverBookingId, setActivePopoverBookingId] = useState<
    string | null
  >(null);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Show skeleton when initializing, transitioning, or any bookings data is undefined
  // Use useMemo to prevent unnecessary recalculations and ensure stable skeleton display
  const showSkeleton = useMemo(() => {
    return (
      isInitialising ||
      isViewTransitioning ||
      bookings === undefined ||
      !hasInitiallyLoaded
    );
  }, [isInitialising, isViewTransitioning, bookings, hasInitiallyLoaded]);

  // Control hasInitiallyLoaded state - reset on transitions, set when all data is ready
  useEffect(() => {
    if (isInitialising || isViewTransitioning) {
      setHasInitiallyLoaded(false);
    } else if (
      bookings !== undefined &&
      unfilteredBookings !== undefined &&
      filteredBookings !== undefined
    ) {
      setHasInitiallyLoaded(true);
    }
  }, [
    isInitialising,
    isViewTransitioning,
    bookings,
    unfilteredBookings,
    filteredBookings
  ]);

  /**
   * Bookings changed (on load or refresh) and apply filters
   * Consolidated effect: updates unfilteredBookings and applies filters in one effect
   * Note: Don't check showSkeleton here as it depends on unfilteredBookings/filteredBookings
   */
  useEffect(() => {
    if (!isInitialising && !isViewTransitioning && bookings !== undefined) {
      setUnfilteredBookings(bookings);
      // Apply filters based on selected table or general filters
      if (diaryContext.selectedTable) {
        const filteredBookingsLocal: any[] = [];
        bookings?.forEach((item) => {
          if (item.assignedAreas?.[0]) {
            item.assignedAreas.forEach((assignedArea: { id: string }) => {
              if (assignedArea.id === diaryContext.selectedTable?.id) {
                filteredBookingsLocal.push(item);
              }
            });
          }
        });
        const filtered = filterBookings(
          filteredBookingsLocal,
          initialFilterQuery
        );
        setFilteredBookings(filtered);
      } else {
        const filtered = filterBookings(bookings, initialFilterQuery);
        setFilteredBookings(filtered);
      }
    } else {
      // Clear both when showing skeleton to prevent any flash
      setUnfilteredBookings(undefined);
      setFilteredBookings(undefined);
    }
  }, [
    bookings,
    isInitialising,
    isViewTransitioning,
    diaryContext.selectedTable,
    initialFilterQuery
  ]);

  const handleBookingChanged = (booking: ViewBooking) => {
    addOrUpdateBooking({ booking, diaryContext });
  };

  /**
   * Push and pull the bookings from the server
   */
  const handleRefresh = useCallback(async () => {
    await diaryContext.fetchBookingList?.();
  }, [diaryContext.fetchBookingList]);

  // Memoize the filtered bookings with time dividers to prevent unnecessary recalculations
  const filteredBookingsWithTimeDividers = useMemo(() => {
    if (!filteredBookings) return undefined;

    if (!showTimeDiviers) return filteredBookings;

    return filteredBookings.reduce(function (
      result: any,
      currentBooking: any,
      index: number
    ) {
      const prevBookingHr = filteredBookings[index - 1]?.time;
      const currentBookingHr = currentBooking?.time;

      if (index === 0 || prevBookingHr !== currentBookingHr) {
        result.push({
          isDivider: true,
          time: currentBooking.time
        });
      }

      result.push(currentBooking);
      return result;
    }, []);
  }, [filteredBookings, showTimeDiviers]);

  // Stable height calculation - memoized to prevent unnecessary re-renders
  const getItemHeight = useCallback(
    (index: number) => {
      const item = filteredBookingsWithTimeDividers?.[index];
      if (!item) return BOOKING_CARD_HEIGHT;

      if (item.isDivider) return DIVIDER_HEIGHT;
      if (waitlistBookingsFilter(item)) return WAITLISTED_CARD_HEIGHT;
      return BOOKING_CARD_HEIGHT;
    },
    [filteredBookingsWithTimeDividers]
  );

  // Memoize the item data to prevent unnecessary re-renders
  const itemData = useMemo(
    () => ({
      bookings: filteredBookingsWithTimeDividers,
      venueConfig,
      venueGroupConfig,
      onBookingChanged: handleBookingChanged,
      selectedView: diaryContext.selectedView,
      selectedBooking: diaryContext.selectedBooking,
      setSelectedBooking: (selectedBooking: ViewBooking) => {
        diaryContext.setDiaryContext({
          selectedBooking,
          selectedBookingV4: undefined
        });
      },
      refreshBookings: handleRefresh,
      isOnRunSheet: isOnRunSheet || false,
      disableSwipe: cardItemIsDragging,
      setDisableSwipe: setCardItemIsDragging,
      activePopoverBookingId,
      setActivePopoverBookingId
    }),
    [
      filteredBookingsWithTimeDividers,
      venueConfig,
      venueGroupConfig,
      diaryContext.selectedView,
      diaryContext.selectedBooking,
      handleRefresh,
      isOnRunSheet,
      cardItemIsDragging,
      activePopoverBookingId
    ]
  );

  // Render individual items - optimized to prevent unnecessary re-renders
  const renderItem = useCallback(
    (item: any, index: number, style: React.CSSProperties) => {
      if (item.isDivider) {
        return (
          <Divider
            key={`divider-${item.time}-${index}`}
            style={style}
            item={item}
            isOnRunSheet={isOnRunSheet || false}
          />
        );
      } else {
        return isOnRunSheet || false ? (
          <RunSheetCard
            index={index}
            style={style}
            parent={{ props: itemData }}
            key={item.id || item.offlineId || index}
            refreshBookings={itemData.refreshBookings}
          />
        ) : (
          <DiaryBookingCard
            index={index}
            style={style}
            parent={{ props: itemData }}
            key={item.id || item.offlineId || index}
          />
        );
      }
    },
    [isOnRunSheet, itemData]
  );

  return (
    <div className={DIARY_BOOKING_CARD_LIST_CLASSES}>
      {/* Skeleton overlay - replaces booking list in normal flow to prevent layout shift */}
      {showSkeleton ? (
        <div className={SKELETON_OVERLAY_CLASSES}>
          <div className="flex flex-col gap-2 p-3">
            {/* Show 10-12 skeleton booking cards to fill the space */}
            {Array.from({ length: 12 }, (_, i) => (
              <DiaryBookingCardPlaceholder key={`skeleton-${i}`} index={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className={CONTENT_CLASSES}>
          {/* Booking list - only render when not loading */}
          {filteredBookings && filteredBookings.length > 0 ? (
            <PullToRefresh
              onRefresh={handleRefresh}
              refreshingContent={
                <div className="flex w-full items-center justify-center py-2">
                  <EDSSpinner size="medium" className="pt-0" />
                </div>
              }
              pullingContent=""
              isPullable={!cardItemIsDragging}
              resistance={2}
            >
              <div className="DiaryBookingCardList__Container h-[90%] w-full">
                <VirtualList
                  key={`booking-list-${filteredBookings?.length || 0}`}
                  items={filteredBookingsWithTimeDividers || []}
                  itemHeight={getItemHeight}
                  height={viewportHeight}
                  renderItem={renderItem}
                />
              </div>
            </PullToRefresh>
          ) : null}

          {/* Empty state - only show when not loading and no bookings */}
          {filteredBookings && filteredBookings.length < 1 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[25vh] text-center p-8">
              <CalendarDays className="h-12 w-12 text-muted-foreground mb-6 opacity-50" />
              <h2 className="text-xl font-semibold mb-3">No Bookings Found</h2>
              <p className="text-muted-foreground text-sm max-w-md">
                No bookings have been found for the selected date and filters.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
